//* ---------------------------------------
//  Vendors

import 'details-element-polyfill'
import 'focus-visible'
import 'focus-within-polyfill'
import 'formdata-polyfill'
import 'objectFitPolyfill'
import 'whatwg-fetch'

import React from 'react'
import ReactDOM from 'react-dom'
import Store from 'statux'
import { BrowserRouter } from 'react-router-dom'
import { supportedProperty } from 'css-vendor'

//* ---------------------------------------
//  Routes

import Routes from './routes/Routes'

//* ---------------------------------------
//  Utils

import authProvider from './utils/auth-provider'
import { getCookie } from './utils/cookies'
import { setCssVar } from './utils/css'
import {
    getBrowserClassList,
    getScrollbarWidth,
    isTouchDevice
} from './utils/dom'
import { fetchClient } from './utils/fetch'
import { graphqlClient, gql } from './utils/gql'

//* ---------------------------------------
//  Feature Detection

// Browser
getBrowserClassList()
    .filter(str => !document.documentElement.classList.contains(str))
    .forEach(str => document.documentElement.classList.add(str))

// CSS Pointer Events Detection
document.body.classList
    .add(`${supportedProperty('pointer-events')
        ? ''
        : 'no-'}csspointerevents`)

// Touch Detection
document.body.classList
    .add(`${isTouchDevice()
        ? ''
        : 'no-'}touch`)

document.body.classList
    .add(`${getScrollbarWidth()
        ? ''
        : 'no-'}scrollbarwidth`)

// Scrollbar width, Overlay is 0
setCssVar('--scrollbar-width',
    `${getScrollbarWidth() || 0}px`)

// Manual scroll restoration on history state
if (window.history.scrollRestoration)
    window.history.scrollRestoration = 'manual'

//* ---------------------------------------
//  Render

// Only used to in dev for HMR
window._catapult = window._catapult
    || { init: true, authProvider: {} }

const getToday = (now = new Date()) => `${
    now.getFullYear()}-${
    now.getMonth() + 1}-${
    now.getDate()}`

Promise
    .all([
        // Fetch global components, routes and options
        fetchClient
            .get(`/api/rest/global.json${
                window.timestamp
                    ? `?v=${window.timestamp}`
                    : ''}`)
            .then((fulfilled) => Object.keys(fulfilled)
                .filter(key => key !== 'type')
                .map(key => ({
                    [key]: key !== 'data'
                        ? fulfilled[key]
                        : (fulfilled[key] || [])
                            .map(obj => Object.assign(obj, {
                                slug: `${
                                    obj.type.substring(0, 1).toLowerCase()}${
                                    obj.type.substring(1)}`
                            }))
                            .filter(obj => obj.modified
                                ? !getCookie(`${obj.slug}-${obj.modified}`)
                                : obj)
                            .reduce((acc, curr) => {
                                if (!curr.single
                                        && !acc.globalComponents)
                                    acc.globalComponents = []

                                if (!curr.single)
                                    acc.globalComponents.push(curr)

                                return Object.assign(acc, {
                                    [curr.slug]: curr.single
                                        ? Object.assign(
                                            acc[curr.slug] || {},
                                            curr)
                                        : null
                                })
                            }, {
                                globalHeader: {
                                    sticky: false,
                                    stickyDisabled: false,
                                    stickyOverlay: false
                                },
                                modal: null
                            })
                }))
                .reduce((acc, curr) => Object.assign(
                    acc,
                    curr.data || curr || {}), {}))
            .catch(err => {
                console.warn(err)
                return {}
            }),

        // Fetch Advanced Membership Plan and Open EBPC Contests
        graphqlClient
            .request(gql`
                query {
                    list_subscription_plans (
                        payload: {
                            name: "advanced membership",
                            offset: 0,
                            limit: 1,
                            status: "ACTIVE"
                        }
                    ) {
                        currency
                        description
                        id
                        name
                        price
                        setupFee
                    }

                    ebpc_contests (
                        order_by: { start_date: asc }
                        where: {
                            _and: [
                                { start_date: { _lte: "${getToday()}" } },
                                { end_date: { _gte: "${getToday()}" } }
                            ]
                        },
                    ) {
                        id
                        start_date
                        end_date
                        ebpc_applications_end_date
                        ebpc_assignments_start_date
                        ebpc_assignments_end_date
                        ebpc_reviews_start_date
                        ebpc_reviews_end_date
                        ebpc_finals_start_date
                        ebpc_finals_end_date
                        created_at
                        updated_at
                    }
                }
            `)
            .then((fulfilled) => {
                // Advanced membership
                const advancedMembershipPlan
                    = fulfilled.list_subscription_plans[0] || false

                let advancedMembershipPrice
                    = (advancedMembershipPlan || {}).price || ''

                if (advancedMembershipPrice
                        && advancedMembershipPlan.currency) {
                    const currency = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: advancedMembershipPlan.currency || 'USD'
                    })

                    advancedMembershipPrice = `${currency
                        .format(parseFloat(advancedMembershipPrice))}`
                        .replace(/\.00$/, '')
                }

                // EBPC
                const ebpcContest = fulfilled.ebpc_contests[0] || false

                const ebpcContestEndDate = ebpcContest
                    && new Date(ebpcContest.end_date)

                if (ebpcContestEndDate)
                    ebpcContestEndDate
                        .setTime(ebpcContestEndDate.getTime()
                            + (ebpcContestEndDate.getTimezoneOffset()
                                * 60000))

                const ebpcApplicationsEndDate = ebpcContest
                    && new Date(ebpcContest.ebpc_applications_end_date)

                if (ebpcApplicationsEndDate)
                    ebpcApplicationsEndDate
                        .setTime(ebpcApplicationsEndDate.getTime()
                            + (ebpcApplicationsEndDate.getTimezoneOffset()
                                * 60000))

                const ebpcAssignmentsStartDate = ebpcContest
                    && new Date(ebpcContest.ebpc_assignments_start_date
                        || ebpcContest.start_date)

                if (ebpcAssignmentsStartDate)
                    ebpcAssignmentsStartDate
                        .setTime(ebpcAssignmentsStartDate.getTime()
                            + (ebpcAssignmentsStartDate.getTimezoneOffset()
                                * 60000))

                const ebpcAssignmentsEndDate = ebpcContest
                        && ebpcContest.ebpc_assignments_end_date
                    ? new Date(ebpcContest.ebpc_assignments_end_date)
                    : ebpcContestEndDate

                if (ebpcAssignmentsEndDate
                        && ebpcAssignmentsEndDate.getTime()
                            !== ebpcContestEndDate.getTime())
                    ebpcAssignmentsEndDate
                        .setTime(ebpcAssignmentsEndDate.getTime()
                            + (ebpcAssignmentsEndDate.getTimezoneOffset()
                                * 60000))

                const ebpcReviewsStartDate = ebpcContest
                        && ebpcContest.ebpc_reviews_start_date
                    ? new Date(ebpcContest.ebpc_reviews_start_date)
                    : (ebpcApplicationsEndDate
                        ? new Date(ebpcApplicationsEndDate)
                        : null)

                if (ebpcReviewsStartDate
                        && ebpcApplicationsEndDate
                        && ebpcReviewsStartDate.getTime()
                            === ebpcApplicationsEndDate.getTime())
                    ebpcReviewsStartDate
                        .setDate(ebpcReviewsStartDate.getDate() + 1)
                else if (ebpcReviewsStartDate
                        && (!ebpcApplicationsEndDate
                                || ebpcReviewsStartDate.getTime()
                                    !== ebpcApplicationsEndDate.getTime()))
                    ebpcReviewsStartDate
                        .setTime(ebpcReviewsStartDate.getTime()
                            + (ebpcReviewsStartDate.getTimezoneOffset()
                                * 60000))

                const ebpcReviewsEndDate = ebpcContest
                        && ebpcContest.ebpc_reviews_end_date
                    ? new Date(ebpcContest.ebpc_reviews_end_date)
                    : (ebpcAssignmentsStartDate
                        ? new Date(ebpcAssignmentsStartDate)
                        : null)

                if (ebpcReviewsEndDate
                        && ebpcAssignmentsEndDate
                        && ebpcReviewsEndDate.getTime()
                            === ebpcAssignmentsStartDate.getTime())
                    ebpcReviewsEndDate
                        .setDate(ebpcReviewsEndDate.getDate() - 1)
                else if (ebpcReviewsEndDate
                        && (!ebpcAssignmentsStartDate
                            || ebpcReviewsEndDate.getTime()
                                !== ebpcAssignmentsStartDate.getTime()))
                    ebpcReviewsEndDate
                        .setTime(ebpcReviewsEndDate.getTime()
                            + (ebpcReviewsEndDate.getTimezoneOffset()
                                * 60000))

                const ebpcFinalsStartDate = ebpcContest
                    && ebpcContest.ebpc_finals_start_date
                    && new Date(ebpcContest.ebpc_finals_start_date)

                const ebpcFinalsEndDate = ebpcFinalsStartDate
                    && ebpcContest.ebpc_finals_end_date
                    && new Date(ebpcContest.ebpc_finals_end_date)

                const months = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ]

                const ebpcContestEndDateStr = ebpcContestEndDate
                    && `${months[ebpcContestEndDate.getMonth()]} ${
                        ebpcContestEndDate.getDate()}, ${
                        ebpcContestEndDate.getFullYear()}`

                const ebpcApplicationsEndDateStr = ebpcApplicationsEndDate
                    && `${months[ebpcApplicationsEndDate.getMonth()]} ${
                        ebpcApplicationsEndDate.getDate()}, ${
                        ebpcApplicationsEndDate.getFullYear()}`

                const ebpcAssignmentsStartDateStr = ebpcAssignmentsStartDate
                    && `${months[ebpcAssignmentsStartDate.getMonth()]} ${
                        ebpcAssignmentsStartDate.getDate()}, ${
                        ebpcAssignmentsStartDate.getFullYear()}`

                const ebpcAssignmentsEndDateStr = ebpcAssignmentsEndDate
                    && `${months[ebpcAssignmentsEndDate.getMonth()]} ${
                        ebpcAssignmentsEndDate.getDate()}, ${
                        ebpcAssignmentsEndDate.getFullYear()}`

                const ebpcReviewsStartDateStr = ebpcReviewsStartDate
                    && `${months[ebpcReviewsStartDate.getMonth()]} ${
                        ebpcReviewsStartDate.getDate()}, ${
                        ebpcReviewsStartDate.getFullYear()}`

                const ebpcReviewsEndDateStr = ebpcReviewsEndDate
                    && `${months[ebpcReviewsEndDate.getMonth()]} ${
                        ebpcReviewsEndDate.getDate()}, ${
                        ebpcReviewsEndDate.getFullYear()}`

                const ebpcFinalsStartDateStr = ebpcFinalsStartDate
                    && `${months[ebpcFinalsStartDate.getMonth()]} ${
                        ebpcFinalsStartDate.getDate()}, ${
                        ebpcFinalsStartDate.getFullYear()}`

                const ebpcFinalsEndDateStr = ebpcFinalsEndDate
                    && `${months[ebpcFinalsEndDate.getMonth()]} ${
                        ebpcFinalsEndDate.getDate()}, ${
                        ebpcFinalsEndDate.getFullYear()}`

                if (ebpcContestEndDate)
                    ebpcContestEndDate
                        .setDate(ebpcContestEndDate.getDate() + 1)

                if (ebpcApplicationsEndDate)
                    ebpcApplicationsEndDate
                        .setDate(ebpcApplicationsEndDate.getDate() + 1)

                if (ebpcAssignmentsEndDate)
                    ebpcAssignmentsEndDate
                        .setDate(ebpcAssignmentsEndDate.getDate() + 1)

                if (ebpcReviewsEndDate)
                    ebpcReviewsEndDate
                        .setDate(ebpcReviewsEndDate.getDate() + 1)

                if (ebpcFinalsEndDate)
                    ebpcFinalsEndDate
                        .setDate(ebpcFinalsEndDate.getDate() + 1)

                const ebpcApplicationsOpen = ebpcContest
                    && (new Date()).getTime()
                        <= ebpcApplicationsEndDate.getTime()

                const ebpcAssignmentsOpen = ebpcAssignmentsStartDate
                    && ebpcAssignmentsEndDate
                    && (new Date()).getTime()
                        >= ebpcAssignmentsStartDate.getTime()
                    && (new Date()).getTime()
                        <= ebpcAssignmentsEndDate.getTime()

                const ebpcReviewsOpen = ebpcReviewsStartDate
                    && ebpcReviewsEndDate
                    && (new Date()).getTime()
                        >= ebpcReviewsStartDate.getTime()
                    && (new Date()).getTime()
                        <= ebpcReviewsEndDate.getTime()

                const ebpcFinalsOpen = ebpcFinalsStartDate
                    && ebpcFinalsEndDate
                    && (new Date()).getTime()
                        >= ebpcFinalsStartDate.getTime()
                    && (new Date()).getTime()
                        <= ebpcFinalsEndDate.getTime()

                return ({
                    advancedMembershipPlan,
                    advancedMembershipPrice,
                    ebpcContest,
                    ebpcContestEndDateStr,
                    ebpcApplicationsOpen,
                    ebpcApplicationsEndDate,
                    ebpcApplicationsEndDateStr,
                    ebpcAssignmentsOpen,
                    ebpcAssignmentsStartDateStr,
                    ebpcAssignmentsEndDateStr,
                    ebpcReviewsOpen,
                    ebpcReviewsStartDateStr,
                    ebpcReviewsEndDateStr,
                    ebpcFinalsOpen,
                    ebpcFinalsStartDateStr,
                    ebpcFinalsEndDateStr
                })
            })
            .catch(err => {
                console.warn(err)

                return ({
                    advancedMembershipPlan: false,
                    advancedMembershipPrice: '',
                    ebpcContest: false,
                    ebpcContestEndDateStr: false,
                    ebpcApplicationsOpen: false,
                    ebpcApplicationsEndDate: false,
                    ebpcApplicationsEndDateStr: false,
                    ebpcAssignmentsOpen: false,
                    ebpcAssignmentsStartDateStr: false,
                    ebpcAssignmentsEndDateStr: false,
                    ebpcReviewsOpen: false,
                    ebpcReviewsStartDateStr: false,
                    ebpcReviewsEndDateStr: false,
                    ebpcFinalsOpen: false,
                    ebpcFinalsStartDateStr: false,
                    ebpcFinalsEndDateStr: false
                })
            })
    ])
    .then(async (fulfilled) => {
        const state = fulfilled
            .reduce((acc, curr) => Object.assign(acc, curr || {}), {})

        const { init } = window._catapult

        if (init)
            await authProvider.refreshToken(true)

        window._catapult.init = false

        const render = () => ReactDOM.render(
            <React.StrictMode>
                <Store
                    foundertracAccessToken={null}
                    advancedMembershipAt={null}
                    user={authProvider.getIdentity()}
                    {...state}
                >
                    <BrowserRouter>
                        <Routes />
                    </BrowserRouter>
                </Store>
            </React.StrictMode>,
            document.getElementById('root'))

        const renderTimeout = import.meta.hot
                && !init
            ? Math.floor((Math.random() * 999) + 0)
            : 0

        return setTimeout(render, renderTimeout)
    })
    .catch(err => {
        console.warn(err)
        return {}
    })

//* ---------------------------------------
//  Global Events

let onScrollDebounced

window
    .addEventListener('scroll', () => {
        if (document.documentElement.style.overscrollBehaviorY)
            document.documentElement.style.overscrollBehaviorY = ''

        if (onScrollDebounced)
            clearTimeout(onScrollDebounced)

        onScrollDebounced = setTimeout(() =>
            window.history
                .replaceState(Object.assign(window.history.state || {}, {
                    scrollY: window.scrollY
                }), null, window.location.href), 200)
    })

let onResizeDebounced

window
    .addEventListener('resize', () => {
        if (document.documentElement.style.overscrollBehaviorY)
            document.documentElement.style.overscrollBehaviorY = ''

        window.objectFitPolyfill()

        const resizeAttr = 'data-resizing'

        if (!document.body.getAttribute(resizeAttr))
            document.body.setAttribute(resizeAttr, true)

        if (onResizeDebounced)
            clearTimeout(onResizeDebounced)

        onResizeDebounced = setTimeout(() =>
            document.body.removeAttribute(resizeAttr), 200)
    })

// Allow pull-to-refresh without scroll-chaining or glow side-effect
// Only Android Chrome supports overscroll-behavior-y on documentElement
window
    .addEventListener('touchstart', () => !window.scrollY
        && !document.documentElement.style.overscrollBehaviorY
        && (document.documentElement.style.overscrollBehaviorY = 'auto'))

//* ---------------------------------------
//  HMR

if (import.meta.hot) {
    import.meta.hot
        .on('vite:beforeUpdate', () =>
            (window._catapult.authProvider = {
                token: authProvider.token,
                refreshSession: authProvider.refreshSession,
                refreshInterval: authProvider.refreshInterval
            }))

    import.meta.hot.accept()
}
